export const currencyWithPrefix = (value: number) => {
    const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);

    return formattedValue;
};

export const concurrencyWithoutPrefix = (value: number) => {
    const formattedValue = currencyWithPrefix(value);
    return formattedValue.replace(/^R\$\s?/, '');
}

export const currencyToNumber = (value: string) => {
    if (!value) return 0;

    const numericString = value.replace(/[^\d,-]/g, '').replace('.', '').replace(/,/g, '.');
    const numberValue = parseFloat(numericString);

    return isNaN(numberValue) ? 0 : numberValue;
}

// Variante para valores em formato americano (vírgula = milhar, ponto = decimal
// - ex: "66,646.75", "994.727640190"), usada quando a corretora exporta a
// planilha nesse formato (ex: XP) em vez do formato brasileiro assumido por
// currencyToNumber acima. Aplicar a função errada silenciosamente infla o
// valor em ~100x pra qualquer número com casas decimais (ex: "12.70" vira
// 1270 via currencyToNumber) - não são intercambiáveis.
export const currencyToNumberUs = (value: string) => {
    if (!value) return 0;

    const numericString = value.replace(/[^\d.-]/g, '');
    const numberValue = parseFloat(numericString);

    return isNaN(numberValue) ? 0 : numberValue;
}

// Variante pra quando o formato (BR ou US) não é conhecido de antemão e pode
// variar linha a linha - caso da Nikos, onde o número exibido depende da
// configuração regional do Excel de quem copiou (mesma "memória de cálculo"
// mostrou "2,000.00" numa amostra e "1.141.000,00" noutra - confirmado
// olhando o number_format real da célula, que é regional-dependente:
// "#,##0.00"). Heurística: o ÚLTIMO separador (`,` ou `.`) encontrado é o
// decimal; o outro, se houver, é milhar e é descartado. Com um separador só,
// assume decimal (não tenta adivinhar milhar sem o outro símbolo como pista).
//
// Desde 2026-09-09 é o parser padrão de TODAS as corretoras (não só Nikos): a
// suposição fixa de formato por corretora quebrou em produção quando XP e
// PicPay, marcadas como formato americano, mandaram planilha em formato BR -
// "12,00" virou 1200 e o INSERT estourou o NUMERIC(15,6) do banco.
export const parseAmbiguousDecimal = (value: string): number => {
    if (!value) return 0;

    const trimmed = value.trim();
    const lastComma = trimmed.lastIndexOf(',');
    const lastDot = trimmed.lastIndexOf('.');

    let numericString: string;
    if (lastComma === -1 && lastDot === -1) {
        numericString = trimmed.replace(/[^\d-]/g, '');
    } else if (lastComma > lastDot) {
        // vírgula é o decimal (formato BR) - ponto(s) antes dela são milhar e
        // já são removidos pelo replace abaixo. Mas quando o único separador
        // presente é a vírgula, em múltiplas ocorrências (ex: "1,234,567",
        // agrupamento americano sem ponto decimal nenhum), vírgulas ANTES da
        // última também são milhar - sem descartá-las, o replace global vira
        // "1.234.567" e parseFloat para no primeiro ponto extra (bug real:
        // review do Copilot em cima do fix anterior, mesma classe de erro do
        // ramo "ponto é decimal" abaixo, só que espelhado pra vírgula).
        const digitsAndCommas = trimmed.replace(/[^\d,-]/g, '');
        const lastCommaIndex = digitsAndCommas.lastIndexOf(',');
        numericString = (lastCommaIndex === -1
            ? digitsAndCommas
            : digitsAndCommas.slice(0, lastCommaIndex).replace(/,/g, '') + digitsAndCommas.slice(lastCommaIndex)
        ).replace(',', '.');
    } else {
        // ponto é o decimal (formato US) - vírgula(s) antes dele são milhar e
        // já são removidas pelo replace abaixo. Mas quando o único separador
        // presente é o ponto (ex: "1.234.567", sem vírgula nenhuma), pontos
        // ANTES do último também são milhar - sem descartá-los, parseFloat
        // parava no primeiro ponto extra e "1.234.567" virava 1.234 (bug real:
        // corrompia PU/financeiro de planilhas BR sem parte decimal).
        const digitsAndDots = trimmed.replace(/[^\d.-]/g, '');
        const lastDotIndex = digitsAndDots.lastIndexOf('.');
        numericString = lastDotIndex === -1
            ? digitsAndDots
            : digitsAndDots.slice(0, lastDotIndex).replace(/\./g, '') + digitsAndDots.slice(lastDotIndex);
    }

    const numberValue = parseFloat(numericString);
    return isNaN(numberValue) ? 0 : numberValue;
}

// Variante da heurística acima para campos INTEIROS (hoje só quantidade). A
// regra "último separador = decimal" está certa pra valor/taxa, mas erraria
// por 1000x em quantidade: "2.787" em formato BR é 2787 unidades, não 2,787.
// Como quantidade fracionária não existe no domínio, aqui um separador
// SOZINHO seguido de exatamente 3 dígitos é tratado como milhar. Essa regra
// NÃO pode valer pros campos decimais: "13,604%" (DI Futuro da Nubank) é
// legitimamente 13,604 e viraria 13604.
export const parseIntegerAmount = (value: string): number => {
    if (!value) return 0;

    const trimmed = value.trim();
    // Todo o valor é um agrupamento de milhar consistente (mesmo separador,
    // repetido em grupos de exatamente 3 dígitos) - cobre tanto um separador só
    // ("2.787") quanto múltiplos grupos ("1.234.567", "1,234,567"). Bug real:
    // a versão anterior só reconhecia essa regra com EXATAMENTE um separador -
    // "1.234.567" caía no parser ambíguo e virava 1.235 (arredondado).
    if (/^-?\d{1,3}([.,]\d{3})+$/.test(trimmed)) {
        return parseInt(trimmed.replace(/[.,]/g, ''), 10);
    }

    // Não bate com o padrão de milhar reconhecido: preserva o valor (sem
    // arredondar) para quem chama rejeitar frações de verdade - quantidade é
    // sempre inteira no domínio. Bug real: Math.round aqui deixava "2,5"/"2.5"
    // virarem 3 e passarem como quantidade válida, sem sinalizar erro nenhum.
    return parseAmbiguousDecimal(trimmed);
};